/**
 * @fliisi/pollen — Themes
 *
 * Semantic color mappings that give meaning to raw tokens.
 * Components reference theme values (e.g. `bg="$background"`)
 * instead of raw colors — so swapping themes swaps everything.
 *
 * Two themes:
 *   - garden: Buyer/seller flows (sage green)
 *   - brick:  Merchant/store flows (terracotta)
 *
 * Each theme also has light/dark variants (dark is stubbed for now).
 */
import { palette } from './tokens'

// ─── Base (shared between garden & brick) ────────────────────────────────

const baseLight = {
  // Backgrounds (warm stone / oatmeal base)
  background: '#F2F0E9',             // Warm Stone (live: background.primary)
  backgroundSecondary: palette.gray200,
  backgroundCard: palette.white,
  backgroundModal: palette.white,
  backgroundOverlay: 'rgba(62, 66, 58, 0.4)',
  backgroundInput: palette.white,

  // Text
  color: palette.gray900,            // Default text color
  colorSecondary: palette.gray500,
  colorMuted: palette.gray400,
  colorInverse: palette.white,

  // Borders
  borderColor: palette.gray200,
  borderColorHover: palette.gray300,
  borderColorPress: palette.gray300,
  borderColorLight: 'rgba(224, 226, 219, 0.5)',
  borderColorFocus: palette.garden400,

  // Pseudo-state tokens (Tamagui resolves these automatically for hoverStyle/pressStyle)
  backgroundHover: palette.gray100,
  backgroundPress: palette.gray200,
  colorHover: palette.gray900,
  colorPress: palette.gray900,

  // Status
  colorSuccess: palette.success,
  colorWarning: palette.warning,
  colorError: palette.error,
  colorInfo: palette.info,

  // Shadows (Tamagui handles these differently, but we define the colors)
  shadowColor: palette.gray900,
}

// ─── Garden Theme (Seller/Buyer — Sage Green) ────────────────────────────

export const gardenLight = {
  ...baseLight,

  // Brand
  colorPrimary: palette.garden400,
  colorPrimaryLight: palette.garden300,
  colorPrimaryDark: palette.garden500,
  colorPrimaryMuted: palette.garden50,
  colorSecondaryAccent: palette.brick400,

  // Interactive
  colorLink: palette.garden400,
  colorFocus: palette.garden400,
  backgroundActive: palette.garden100,
  borderColorFocus: palette.garden400,
}

export const gardenDark = {
  ...gardenLight,
  // TODO: dark mode values
  // background: '#1a1a1a',
  // color: '#f0f0f0',
  // etc.
}

// ─── Brick Theme (Merchant/Store — Terracotta) ───────────────────────────

export const brickLight = {
  ...baseLight,

  // Brand
  colorPrimary: palette.brick400,
  colorPrimaryLight: palette.brick300,
  colorPrimaryDark: palette.brick500,
  colorPrimaryMuted: palette.brick50,
  colorSecondaryAccent: palette.garden400,  // Sage as accent in merchant context

  // Interactive
  colorLink: palette.brick400,
  colorFocus: palette.brick400,
  backgroundActive: palette.brick100,
  borderColorFocus: palette.brick400,
}

export const brickDark = {
  ...brickLight,
  // TODO: dark mode values
}

// ─── All themes map ──────────────────────────────────────────────────────

export const themes = {
  // Base light/dark themes (required by Tamagui v2 as fallback)
  light: gardenLight,
  dark: gardenDark,
  // Named themes
  garden: gardenLight,
  garden_dark: gardenDark,
  brick: brickLight,
  brick_dark: brickDark,
} as const
